import { logger } from 'shared/lib'
import { ContactsApi } from 'entities/Contacts/api/contacts'
import { ChatbotApi } from 'entities/Chatbot/api/chatbot'
import { ChatbotListParams } from 'entities/Chatbot/api/chatbotTypes'
import { ChatbotStatus } from 'entities/Chatbot/model/type'
import { BroadcastApi } from 'entities/Broadcast/api/broadcast'
import { MessagesAnalyticsApi } from 'entities/Analytics/api/messagesAnalytics'
import { CallsAnalyticsApi } from 'entities/Analytics/api/callsAnalytics'
import { type IAnalyticsParams } from 'entities/Analytics/api/types'
import {
  deriveOnboardingSteps,
  type OnboardingSignals,
  type OnboardingStatus,
} from 'entities/WebMcp/lib/onboardingSignals'

// Each source degrades to `null` (→ reported as `unknown`) rather than failing
// the whole status read, so one flaky endpoint never blocks the co-pilot.
const safe = async (read: () => Promise<boolean>): Promise<boolean | null> => {
  try {
    return await read()
  } catch (error) {
    logger.error(error)
    return null
  }
}

const pad = (n: number): string => String(n).padStart(2, '0')
const ymd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

// Onboarding targets new users, so a first message/call is recent; a 2-year
// window catches it while keeping the analytics query bounded.
const analyticsRange = (): IAnalyticsParams => {
  const to = new Date()
  const from = new Date()
  from.setFullYear(from.getFullYear() - 2)
  return {
    from: ymd(from),
    to: ymd(to),
    interval: 'month',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
}

const statValue = (statistic: ReadonlyArray<{ key: string; value: number }>, key: string): number =>
  statistic.find((s) => s.key === key)?.value ?? 0

/** Fan out the existing read APIs that back each onboarding step (parallel, fault-tolerant). */
export const fetchOnboardingSignals = async (): Promise<OnboardingSignals> => {
  const [hasSentMessage, hasMadeCall, hasContacts, hasBroadcast, hasTextbot] = await Promise.all([
    safe(async () => {
      // Analytics requires preload (server computes) before get (retrieves) —
      // every analytics page store does this; get-alone returns no statistic.
      const params = analyticsRange()
      await MessagesAnalyticsApi.preloadAnalytics(params)
      const { data } = await MessagesAnalyticsApi.getAnalytics(params)
      return statValue(data.statistic, 'total_outbound') > 0
    }),
    safe(async () => {
      const params = analyticsRange()
      await CallsAnalyticsApi.preloadAnalytics(params)
      const { data } = await CallsAnalyticsApi.getAnalytics(params)
      // `total` aggregates inbound too, so a received call would falsely
      // complete this step; the step is about placing a call.
      return statValue(data.statistic, 'outbound_calls') > 0
    }),
    safe(async () => {
      const { data } = await ContactsApi.getContactsSearch({ term: '', page: 1, per_page: 1 })
      // Every new org is seeded with the user's own contact, so a count of 1 means
      // no contact has actually been added yet. Require more than the self-contact
      // so the "Add your contacts" onboarding step still nudges a brand-new user.
      return data.total > 1
    }),
    safe(async () => {
      // Mirror broadcastStore.checkEmptyStateBroadcast exactly — length:1 is
      // rejected (422); the empty-state check uses length:10.
      const { data } = await BroadcastApi.getBroadcasts({ type: 'all', length: 10, page: 1 })
      return data.data.length > 0
    }),
    safe(async () => {
      // New orgs are seeded with default agents that are inactive (Draft), so a
      // raw count is always > 0. The meaningful step is enabling one, so ask the
      // server for Active agents only.
      const { data } = await ChatbotApi.getChatbotList({
        limit: 1,
        page: 1,
        [ChatbotListParams.Status]: ChatbotStatus.Active,
      })
      return data.meta.total > 0
    }),
  ])
  return { hasSentMessage, hasMadeCall, hasContacts, hasBroadcast, hasTextbot }
}

export const computeOnboardingStatus = async (): Promise<OnboardingStatus> =>
  deriveOnboardingSteps(await fetchOnboardingSignals())

// Session-cached variant so the launcher badge and the chat panel share a single
// onboarding fetch rather than each fanning out the read APIs. Cached for the
// session (the panel already fetches once); call reset after a change if needed.
let cachedStatus: Promise<OnboardingStatus> | null = null

export const getOnboardingStatusCached = (): Promise<OnboardingStatus> => {
  if (!cachedStatus) {
    // Drop the cache when nothing could be read (every source failed), so a
    // transient outage does not suppress onboarding for the whole session.
    cachedStatus = computeOnboardingStatus().then(
      (status) => {
        if (status.steps.every((s) => s.status === 'unknown')) cachedStatus = null
        return status
      },
      (error) => {
        cachedStatus = null
        throw error
      }
    )
  }
  return cachedStatus
}

export const resetOnboardingStatusCache = (): void => {
  cachedStatus = null
}
